import {
  PayloadAction,
  configureStore,
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createReducer,
  createSelector,
} from "@reduxjs/toolkit"
import { CalendarApi } from "@fullcalendar/core/index.js"
import { toast } from "sonner"
import { TFunction } from "i18next"
import { Brand, identity } from "ts-brand"

import {
  calculateEventStartCalendarTimezoneCoerced,
  getTimezoneAbbreviation,
  translateCoercedTime,
} from "@/lib/timetableZonedDatetime"
import { createGetColour } from "@/constants/priorityColours"

import { typedEntries } from "@/types/helpers"
import { createContext } from "react"
import {
  createDispatchHook,
  createSelectorHook,
  ReactReduxContextValue,
} from "react-redux"
import { queryOverlaps } from "@/lib/interval-overlaps"
import { CycleEvent } from "@/types/cycle-event"

type PageStore = ReturnType<typeof createPageStore>
type PageState = ReturnType<PageStore["getState"]>
export type PageDispatch = PageStore["dispatch"]
export type PageThunk<T = void> = (
  dispatch: PageDispatch,
  getState: () => PageState,
) => T
const createPageSelector = createSelector.withTypes<PageState>()
export const PageReduxContext =
  createContext<ReactReduxContextValue<any> | null>(null)
export const usePageSelector =
  createSelectorHook(PageReduxContext).withTypes<PageState>()
export const usePageDispatch =
  createDispatchHook(PageReduxContext).withTypes<PageDispatch>()

type AddTimetablePriorityPayload = {
  customClassId: CustomClassId
}

interface TimetableSessionDropPayload {
  timetableSessionId: TimetableSessionId
  sessionDetails: {
    startTime: string
    day: number
  } | null
}

interface RemoveTimetableSessionPayload {
  timetableSessionId: TimetableSessionId
}

interface RemoveTimetablePriorityPayload {
  timetablePriorityId: TimetablePriorityId
}

interface AdjustTimetablePriorityPayload {
  direction: "up" | "down"
  timetablePriorityId: TimetablePriorityId
}

export interface TimetableLoadedPayload {
  timetable_priorities: Array<{
    custom_class_id: number
    timetable_sessions: Array<{
      session_id: number
      day: number | null
      start_time: string | null
    }>
  }>
  custom_classes: Array<{
    subject_code: string
    name: string
    id: number
    live_sessions: Array<{ duration: number; id: number }>
    timezone_id: string
    cycle_length: number
  }>
}

export type TimetablePriorityId = Brand<string, "TimetablePriorityId">
export const TimetablePriorityId = () =>
  identity<TimetablePriorityId>(crypto.randomUUID())
type TimetablePriority = { id: TimetablePriorityId; customClass: CustomClass }
const timetablePriorityAdapter = createEntityAdapter<TimetablePriority>()

export type TimetableSessionId = Brand<string, "TimetableSessionId">
export const TimetableSessionId = ({
  timetablePriorityId,
  sessionId,
}: {
  timetablePriorityId: TimetablePriorityId
  sessionId: number
}) => identity<TimetableSessionId>(`${timetablePriorityId}:${sessionId}`)
type TimetableSession = {
  sessionDetails: { startTime: string; day: number } | null
  timetablePriorityId: TimetablePriorityId
  session: Session
  customClass: CustomClass
}

const timetableSessionAdapter = createEntityAdapter<
  TimetableSession,
  TimetableSessionId
>({
  selectId: (timetableSession) =>
    TimetableSessionId({
      timetablePriorityId: timetableSession.timetablePriorityId,
      sessionId: timetableSession.session.id,
    }),
})

export type CustomClassId = Brand<number, "customClassId">
export const CustomClassId = ({ customClassId }: { customClassId: number }) =>
  identity<CustomClassId>(customClassId)
type CustomClass = {
  id: number
  name: string
  subjectCode: string
  timezoneId: string
  cycleLength: number
}
const customClassAdapter = createEntityAdapter<CustomClass, CustomClassId>({
  selectId: (customClass) => CustomClassId({ customClassId: customClass.id }),
})

export type SessionId = Brand<number, "SessionId">
export const SessionId = ({ sessionId }: { sessionId: number }) =>
  identity<SessionId>(sessionId)
type Session = {
  id: number
  duration: number
  customClassId: CustomClassId
}
const sessionAdapter = createEntityAdapter<Session, SessionId>({
  selectId: (session) => SessionId({ sessionId: session.id }),
})

async function postTimetable(state: PageState) {
  const body = {
    timetable: {
      timetable_priorities_attributes: state.timetablePriorityOrderedIds
        .map((timetablePriorityId) =>
          timetablePriorityAdapter
            .getSelectors()
            .selectById(state.timetablePriorities, timetablePriorityId),
        )
        .map((timetablePriority) => {
          const timetableSessions = timetableSessionAdapter
            .getSelectors()
            .selectAll(state.timetableSessions)
            .filter(
              (timetableSession) =>
                timetableSession.timetablePriorityId === timetablePriority.id,
            )
          return {
            custom_class_id: timetablePriority.customClass.id,
            timetable_sessions_attributes: timetableSessions
              .filter((timetableSession) => timetableSession.sessionDetails)
              .map((timetableSession) => ({
                session_id: timetableSession.session.id,
                day: timetableSession.sessionDetails!.day,
                start_time: timetableSession.sessionDetails!.startTime,
              })),
          }
        }),
    },
  }

  const token = document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] || ""
  const response = await fetch("./timetable", {
    method: "PUT",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      "X-XSRF-TOKEN": token,
    },
  })

  if (response.ok) return

  throw await response.json()
}

const saveTeacherTimetable = createAsyncThunk<
  Promise<void>,
  { action: PayloadAction<any>; t: TFunction<readonly ["timetables"]> },
  { state: PageState; dispatch: PageDispatch }
>("teacherTimetable/saveTeacherTimetable", async ({ action, t }, thunkAPI) => {
  const previousState = thunkAPI.getState()
  thunkAPI.dispatch(action)

  return await postTimetable(thunkAPI.getState()).catch((error) => {
    toast.error(t(error.error_code))
    thunkAPI.dispatch(stateRestored(previousState))
    throw error
  })
})

export const updateTimetableTimezoneOffset = (calendarApi: CalendarApi) => {
  return (dispatch: PageDispatch, getState: () => PageState) => {
    dispatch(timetableTimezoneOffsetUpdated())
    calendarApi.gotoDate(getState().calendar.startInstant)
  }
}

const timetablePriorityAdded = createAction<AddTimetablePriorityPayload>(
  "timetablePriorityAdded",
)
const timetablePriorityRemoved = createAction<RemoveTimetablePriorityPayload>(
  "timetablePriorityRemoved",
)
const timetableSessionDropped = createAction<TimetableSessionDropPayload>(
  "timetableSessionDropped",
)
const timetableSessionRemoved = createAction<RemoveTimetableSessionPayload>(
  "timetableSessionRemoved",
)
const timetablePriorityAdjusted = createAction<AdjustTimetablePriorityPayload>(
  "timetablePriorityAdjusted",
)
const timetableLoaded = createAction<TimetableLoadedPayload>("timetableLoaded")
const timetableTimezoneUpdated = createAction<{ timezone: string }>(
  "timetableTimezoneUpdated",
)
const timetableTimezoneOffsetUpdated = createAction(
  "timetableTimezoneOffsetUpdated",
)
const stateRestored = createAction<PageState>("stateRestored")

const initialStartInstant = "2025-01-06T00:00:00Z"
const initialTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

const pageReducer = createReducer(
  {
    saving: false,
    calendar: {
      startInstant: initialStartInstant,
      timezone: initialTimezone,
    },
    timetablePriorityOrderedIds: [] as Array<TimetablePriorityId>,
    timetablePriorities: timetablePriorityAdapter.getInitialState(),
    timetableSessions: timetableSessionAdapter.getInitialState(),
    customClasses: customClassAdapter.getInitialState(),
    sessions: sessionAdapter.getInitialState(),
  },
  (builder) => {
    builder.addCase(timetablePriorityAdded, (state, action) => {
      const timetablePriorityId = TimetablePriorityId()
      const customClass = customClassAdapter
        .getSelectors()
        .selectById(state.customClasses, action.payload.customClassId)
      state.timetablePriorityOrderedIds.push(timetablePriorityId)
      timetablePriorityAdapter.addOne(state.timetablePriorities, {
        id: timetablePriorityId,
        customClass,
      })

      timetableSessionAdapter.addMany(
        state.timetableSessions,
        sessionAdapter
          .getSelectors()
          .selectAll(state.sessions)
          .filter(
            (session) => session.customClassId === action.payload.customClassId,
          )
          .map((session) => ({
            sessionDetails: null,
            timetablePriorityId,
            session,
            customClass,
          })),
      )
    })

    builder.addCase(timetablePriorityRemoved, (state, action) => {
      const { timetablePriorityId } = action.payload
      timetablePriorityAdapter.removeOne(
        state.timetablePriorities,
        timetablePriorityId,
      )
      timetableSessionAdapter.removeMany(
        state.timetableSessions,
        timetableSessionAdapter
          .getSelectors()
          .selectAll(state.timetableSessions)
          .filter(
            (timetableSession) =>
              timetableSession.timetablePriorityId === timetablePriorityId,
          )
          .map((timetableSession) =>
            timetableSessionAdapter.selectId(timetableSession),
          ),
      )
      state.timetablePriorityOrderedIds =
        state.timetablePriorityOrderedIds.filter(
          (timetablePriorityId) =>
            timetablePriorityId !== action.payload.timetablePriorityId,
        )
    })

    builder.addCase(timetableSessionDropped, (state, action) => {
      const { timetableSessionId, sessionDetails } = action.payload

      timetableSessionAdapter.updateOne(state.timetableSessions, {
        id: timetableSessionId,
        changes: { sessionDetails },
      })
    })

    builder.addCase(timetableSessionRemoved, (state, action) => {
      const { timetableSessionId } = action.payload
      timetableSessionAdapter.updateOne(state.timetableSessions, {
        id: timetableSessionId,
        changes: { sessionDetails: null },
      })
    })

    builder.addCase(timetablePriorityAdjusted, (state, action) => {
      const { timetablePriorityId, direction } = action.payload
      const thisPriorityIndex =
        state.timetablePriorityOrderedIds.indexOf(timetablePriorityId)
      const otherPriorityIndex =
        direction === "up" ? thisPriorityIndex - 1 : thisPriorityIndex + 1
      const tmp = state.timetablePriorityOrderedIds[thisPriorityIndex]
      state.timetablePriorityOrderedIds[thisPriorityIndex] =
        state.timetablePriorityOrderedIds[otherPriorityIndex]
      state.timetablePriorityOrderedIds[otherPriorityIndex] = tmp
    })

    builder.addCase(timetableLoaded, (state, action) => {
      action.payload.custom_classes.forEach((backendCustomClass) => {
        const customClass = {
          id: backendCustomClass.id,
          name: backendCustomClass.name,
          subjectCode: backendCustomClass.subject_code,
          timezoneId: backendCustomClass.timezone_id,
          cycleLength: backendCustomClass.cycle_length,
        }
        customClassAdapter.addOne(state.customClasses, customClass)

        sessionAdapter.addMany(
          state.sessions,
          backendCustomClass.live_sessions.map((backendSession) => ({
            id: backendSession.id,
            duration: backendSession.duration,
            customClassId: customClassAdapter.selectId(customClass),
          })),
        )
      })

      action.payload.timetable_priorities.forEach((timetable_priority) => {
        const timetablePriorityId = TimetablePriorityId()
        state.timetablePriorityOrderedIds.push(timetablePriorityId)
        const customClass = customClassAdapter.getSelectors().selectById(
          state.customClasses,
          CustomClassId({
            customClassId: timetable_priority.custom_class_id,
          }),
        )

        timetablePriorityAdapter.addOne(state.timetablePriorities, {
          id: timetablePriorityId,
          customClass,
        })

        timetableSessionAdapter.addMany(
          state.timetableSessions,
          timetable_priority.timetable_sessions.map((timetable_session) => ({
            sessionDetails: timetable_session.start_time
              ? {
                  startTime: timetable_session.start_time,
                  day: timetable_session.day!,
                }
              : null,
            timetablePriorityId,
            session: sessionAdapter
              .getSelectors()
              .selectById(
                state.sessions,
                SessionId({ sessionId: timetable_session.session_id }),
              ),
            customClass,
          })),
        )
      })
    })

    builder.addCase(timetableTimezoneUpdated, (state, action) => {
      state.calendar.timezone = action.payload.timezone
    })

    builder.addCase(timetableTimezoneOffsetUpdated, (state) => {
      if (state.calendar.startInstant !== initialStartInstant) {
        state.calendar.startInstant = initialStartInstant
        return
      }

      const transition = Temporal.Instant.from(state.calendar.startInstant)
        .toZonedDateTimeISO(state.calendar.timezone)
        .getTimeZoneTransition("next")
      if (!transition) return

      state.calendar.startInstant =
        transition
          .add({ days: (8 - transition.dayOfWeek) % 7 })
          .startOfDay()
          .toPlainDateTime()
          .toString() + "Z"
    })

    builder.addCase(saveTeacherTimetable.pending, (state) => {
      state.saving = true
    })

    builder.addCase(saveTeacherTimetable.fulfilled, (state) => {
      state.saving = false
    })

    builder.addCase(saveTeacherTimetable.rejected, (state) => {
      state.saving = false
    })

    builder.addCase(stateRestored, (_state, action) => {
      return action.payload
    })
  },
)

export const createPageStore = () =>
  configureStore({
    reducer: pageReducer,
  })

const customClassSelectors = customClassAdapter.getSelectors(
  (state: PageState) => state.customClasses,
)
const timetablePrioritySelectors = timetablePriorityAdapter.getSelectors(
  (state: PageState) => state.timetablePriorities,
)
const timetableSessionSelectors = timetableSessionAdapter.getSelectors(
  (state: PageState) => state.timetableSessions,
)
const sessionSelectors = sessionAdapter.getSelectors(
  (state: PageState) => state.sessions,
)

export const selectTimetableSessionsById =
  timetableSessionSelectors.selectEntities

export const selectCustomClass = (
  state: PageState,
  { customClassId }: { customClassId: CustomClassId },
) => customClassSelectors.selectById(state, customClassId)

export const selectTimetablePriority = (
  state: PageState,
  { timetablePriorityId }: { timetablePriorityId: TimetablePriorityId },
) => timetablePrioritySelectors.selectById(state, timetablePriorityId)

const selectTimetablePriorityOrderedIds = (state: PageState) =>
  state.timetablePriorityOrderedIds

export const selectTimetableSession = (
  state: PageState,
  { timetableSessionId }: { timetableSessionId: TimetableSessionId },
) => timetableSessionSelectors.selectById(state, timetableSessionId)

export const selectTimetableSessionSchedule = (
  state: PageState,
  { timetableSessionId }: { timetableSessionId: TimetableSessionId },
) => selectTimetableSession(state, { timetableSessionId }).sessionDetails

export const selectCustomClassSession = (
  state: PageState,
  { sessionId }: { sessionId: SessionId },
) => sessionSelectors.selectById(state, sessionId)

export const selectCustomClassOptions = createPageSelector(
  [customClassSelectors.selectAll],
  (customClasses) =>
    customClasses.map((customClass) => ({
      value: customClassAdapter.selectId(customClass),
      label: customClass.name,
    })),
)

export const selectCalendar = createPageSelector(
  [(state: PageState) => state.calendar, customClassSelectors.selectAll],
  (calendar, customClasses) => ({
    ...calendar,
    startTemporalInstant: Temporal.Instant.from(calendar.startInstant),
    startTemporalZonedDateTime: Temporal.Instant.from(
      calendar.startInstant,
    ).toZonedDateTimeISO("UTC"),
    cycleLength:
      customClasses.reduce(
        (acc, customClass) =>
          customClass.cycleLength > acc ? customClass.cycleLength : acc,
        0,
      ) || 7,
  }),
)

export const selectPriorityIndex = createPageSelector(
  [
    selectTimetablePriorityOrderedIds,
    (
      _state,
      { timetablePriorityId }: { timetablePriorityId: TimetablePriorityId },
    ) => timetablePriorityId,
  ],
  (timetablePriorityOrderedIds, timetablePriorityId) =>
    timetablePriorityOrderedIds.indexOf(timetablePriorityId),
)

const buildEventTitle = ({
  subjectCode,
  priorityIndex,
  tFunction,
}: {
  subjectCode: string
  priorityIndex: number
  tFunction: TFunction<readonly ["timetables"]>
}) => {
  return tFunction("timetables:edit.events.short_title", {
    subjectCode,
    priority: priorityIndex + 1,
  })
}
export const selectEventTitle = createPageSelector(
  [
    selectCustomClass,
    selectPriorityIndex,
    (
      _state: PageState,
      { tFunction }: { tFunction: TFunction<readonly ["timetables"]> },
    ) => tFunction,
  ],
  (customClass, priorityIndex, tFunction) =>
    buildEventTitle({
      subjectCode: customClass.subjectCode,
      priorityIndex,
      tFunction,
    }),
)

const timetablePrioritiesPerClass = (
  priorities: PageState["timetablePriorities"]["entities"],
  customClassId: CustomClassId,
) => {
  return Object.fromEntries(
    Object.entries(priorities).filter(
      ([_, priority]) => priority.customClass.id === customClassId,
    ),
  )
}

const selectTimetablePrioritiesPerClass = createPageSelector(
  [
    timetablePrioritySelectors.selectEntities,
    (_: PageState, { customClassId }: { customClassId: CustomClassId }) =>
      customClassId,
  ],
  (priorities, customClassId) =>
    timetablePrioritiesPerClass(priorities, customClassId),
)

const selectLocalPriorityIndex = createPageSelector(
  [
    selectTimetablePrioritiesPerClass,
    (
      _state,
      { timetablePriorityId }: { timetablePriorityId: TimetablePriorityId },
    ) => timetablePriorityId,
  ],
  (timetablePrioritiesPerClass, timetablePriorityId) =>
    Object.keys(timetablePrioritiesPerClass).indexOf(timetablePriorityId),
)

const getColours = createGetColour()
export const selectEventColours = createPageSelector(
  [
    selectCustomClass,
    selectLocalPriorityIndex,
    selectTimetablePrioritiesPerClass,
  ],
  (customClass, localPriorityIndex, timetablePrioritiesPerClass) =>
    getColours(
      customClass.name,
      localPriorityIndex,
      Object.keys(timetablePrioritiesPerClass).length,
    ),
)

function computeEventTemporalInformation({
  startTime,
  duration,
  day,
  calendarTimezoneId,
  eventTimezoneId,
  startTemporalZonedDateTime,
  cycleLength,
}: {
  startTime: string
  duration: number
  day: number
  calendarTimezoneId: string
  eventTimezoneId: string
  startTemporalZonedDateTime: Temporal.ZonedDateTime
  cycleLength: number
}) {
  const eventStartPlainTime = Temporal.PlainTime.from(startTime)
  const eventEndPlainTime = eventStartPlainTime.add({ minutes: duration })

  const calendarTimezoneStartCoerced =
    calculateEventStartCalendarTimezoneCoerced({
      startTime,
      day,
      calendarTimezoneId,
      eventTimezoneId,
      startTemporalZonedDateTime,
    })
  const calendarTimezoneStartPlainTime =
    calendarTimezoneStartCoerced.toPlainTime()
  const calendarTimezoneEndPlainTime = calendarTimezoneStartPlainTime.add({
    minutes: duration,
  })

  const calendarTimezoneDay = startTemporalZonedDateTime
    .toPlainDate()
    .until(calendarTimezoneStartCoerced.toPlainDate(), {
      largestUnit: "day",
    }).days
  return {
    eventStartPlainTime,
    eventEndPlainTime,
    eventDay: day,
    calendarTimezoneStartPlainTime,
    calendarTimezoneEndPlainTime,
    calendarTimezoneDay:
      ((calendarTimezoneDay % cycleLength) + cycleLength) % cycleLength,
  }
}

export const selectSessionTemporalInformation = createPageSelector(
  [selectTimetableSession, selectCalendar],
  (
    { session, sessionDetails, customClass },
    { startTemporalZonedDateTime, timezone: calendarTimezoneId, cycleLength },
  ) => {
    if (!sessionDetails) return null

    return {
      duration: session.duration,
      inCalendarTimezone: calendarTimezoneId === customClass.timezoneId,
      timezoneAbbreviation: getTimezoneAbbreviation(customClass.timezoneId),
      ...computeEventTemporalInformation({
        ...sessionDetails,
        duration: session.duration,
        calendarTimezoneId,
        eventTimezoneId: customClass.timezoneId,
        startTemporalZonedDateTime,
        cycleLength,
      }),
    }
  },
)

export const selectFullCalendarEvents = createPageSelector(
  [
    timetablePrioritySelectors.selectEntities,
    timetableSessionSelectors.selectEntities,
    selectTimetablePriorityOrderedIds,
    (
      _state: PageState,
      { tFunction }: { tFunction: TFunction<readonly ["timetables"]> },
    ) => tFunction,
  ],
  (
    timetablePrioritiesById,
    timetableSessionsById,
    timeetablePriorityOrderedIds,
    tFunction,
  ) =>
    typedEntries(timetableSessionsById)
      .filter(([_, timetableSession]) => !!timetableSession.sessionDetails)
      .flatMap(
        ([
          timetableSessionId,
          { timetablePriorityId, session, sessionDetails, customClass },
        ]) => {
          const { startTime, day } = sessionDetails!
          const { duration } = session
          const {
            name: customClassName,
            subjectCode,
            cycleLength,
            timezoneId: eventTimezoneId,
          } = customClass
          const priorityIndex =
            timeetablePriorityOrderedIds.indexOf(timetablePriorityId)
          const prioritiesPerClass = Object.keys(
            timetablePrioritiesPerClass(
              timetablePrioritiesById,
              customClassAdapter.selectId(customClass),
            ),
          )
          const localPriorityIndex =
            prioritiesPerClass.indexOf(timetablePriorityId)
          const numberOfPrioritiesPerClass = prioritiesPerClass.length
          return {
            cycleLength,
            id: timetableSessionId,
            startTime,
            duration,
            day,
            eventTimezoneId,
            eventTitle: buildEventTitle({
              subjectCode,
              priorityIndex,
              tFunction,
            }),
            extendedProps: {
              timetableSessionId,
              colours: getColours(
                customClassName,
                localPriorityIndex,
                numberOfPrioritiesPerClass,
              ),
            },
          }
        },
      ),
)

export const selectTimetablePriorityIds = (state: PageState) =>
  state.timetablePriorityOrderedIds

export const selectOrderedTimetableSessionIds = createPageSelector(
  [
    timetableSessionSelectors.selectAll,
    (
      _state,
      { timetablePriorityId }: { timetablePriorityId: TimetablePriorityId },
    ) => timetablePriorityId,
  ],
  (timetableSessions, timetablePriorityId) => {
    return timetableSessions
      .filter(
        (timetableSession) =>
          timetableSession.timetablePriorityId === timetablePriorityId,
      )
      .slice()
      .sort((a, b) => {
        const aCustomClassSession = a.session
        const bCustomClassSession = b.session

        const aSessionDetails = a.sessionDetails
        const bSessionDetails = b.sessionDetails

        if (aSessionDetails && !bSessionDetails) return -1
        if (!aSessionDetails && bSessionDetails) return 1
        if (aSessionDetails && bSessionDetails)
          return aSessionDetails.day - bSessionDetails.day

        return bCustomClassSession.duration - aCustomClassSession.duration
      })
      .map((timetableSession) =>
        timetableSessionAdapter.selectId(timetableSession),
      )
  },
)

type OverlapRequirement = {
  customClassId: CustomClassId
  timetablePriorityId: TimetablePriorityId
}

type OverlapCycleEvent = CycleEvent<OverlapRequirement, TimetableSessionId>

function createOverlapCycleEvent(
  timetableSessionId: TimetableSessionId,
  {
    sessionDetails,
    customClass,
    session,
    timetablePriorityId,
  }: Pick<
    TimetableSession,
    "sessionDetails" | "customClass" | "session" | "timetablePriorityId"
  >,
): OverlapCycleEvent | null {
  if (sessionDetails === null) return null
  const { cycleLength, timezoneId: eventTimezoneId } = customClass
  const { startTime, day } = sessionDetails
  const { duration } = session
  return {
    cycleLength,
    startTime,
    day,
    duration,
    eventTimezoneId,
    eventTitle: timetableSessionId,
    id: timetableSessionId,
    extendedProps: {
      customClassId: customClassAdapter.selectId(customClass),
      timetablePriorityId,
    },
  }
}

export const loadTimetable =
  (payload: TimetableLoadedPayload): PageThunk<void> =>
  (dispatch, _getState) => {
    dispatch(timetableLoaded(payload))
  }

export const toggleTimezoneOffset = timetableTimezoneOffsetUpdated

export const addTimetablePriority = (
  payload: AddTimetablePriorityPayload,
  t: TFunction<readonly ["timetables"]>,
): PageThunk<void> => {
  return async (dispatch) =>
    await dispatch(
      saveTeacherTimetable({ action: timetablePriorityAdded(payload), t }),
    )
}

export const removeTimetablePriority = (
  payload: RemoveTimetablePriorityPayload,
  t: TFunction<readonly ["timetables"]>,
): PageThunk<void> => {
  return async (dispatch, _getState) => {
    await dispatch(
      saveTeacherTimetable({ action: timetablePriorityRemoved(payload), t }),
    )
  }
}

export const dropTimetableSession = (
  t: TFunction<readonly ["timetables"]>,
  revertCalendarChange: () => void,
  payload: {
    startStr: string
    timetableSessionId: TimetableSessionId
  },
): PageThunk<void> => {
  return async (dispatch, getState) => {
    const { startStr, timetableSessionId } = payload
    const {
      customClass,
      session,
      timetablePriorityId,
      sessionDetails: originalSessionDetails,
    } = selectTimetableSession(getState(), {
      timetableSessionId,
    })
    const calendar = selectCalendar(getState())

    const calendarStart = calendar.startTemporalZonedDateTime.toPlainDate()
    const eventStartInEventTimeZone = translateCoercedTime(
      Temporal.Instant.from(startStr).toZonedDateTimeISO("UTC"),
      {
        fromTimezoneId: calendar.timezone,
        toTimezoneId: customClass.timezoneId,
      },
    )

    const diff = calendarStart.until(eventStartInEventTimeZone.toPlainDate(), {
      largestUnit: "days",
    })
    const cycleLength = customClass.cycleLength
    const day = ((diff.days % cycleLength) + cycleLength) % cycleLength
    const startTime = eventStartInEventTimeZone
      .toPlainTime()
      .toString({ smallestUnit: "minute" })

    const cycleEvent = createOverlapCycleEvent(timetableSessionId, {
      customClass,
      session,
      sessionDetails: {
        startTime,
        day,
      },
      timetablePriorityId,
    }) as OverlapCycleEvent

    const overlapCycleEvents: OverlapCycleEvent[] = timetableSessionSelectors
      .selectAll(getState())
      .flatMap((session) => {
        if (timetableSessionAdapter.selectId(session) === timetableSessionId) {
          return []
        }
        return createOverlapCycleEvent(
          timetableSessionAdapter.selectId(session),
          {
            customClass: session.customClass,
            session: session.session,
            sessionDetails: session.sessionDetails,
            timetablePriorityId: session.timetablePriorityId,
          },
        )
      })
      .filter((e) => e !== null)

    const overlappingSessions = queryOverlaps(
      [cycleEvent, ...overlapCycleEvents],
      calendar.startTemporalInstant,
    )

    const isOverlapping = overlappingSessions.some(
      ([overlappingTimetableSessionId, overlappingCycleEvents]) => {
        if (overlappingTimetableSessionId !== timetableSessionId) {
          return false
        }
        return overlappingCycleEvents.some((overlapCycleEvent) => {
          return (
            overlapCycleEvent.extendedProps.customClassId !== customClass.id ||
            overlapCycleEvent.extendedProps.timetablePriorityId ===
              timetablePriorityId
          )
        })
      },
    )

    if (isOverlapping) {
      toast.info(t("timetables:errors.overlap"))
      revertCalendarChange()

      // required to ensure calendar events recalculate
      dispatch(
        timetableSessionDropped({
          timetableSessionId,
          sessionDetails: originalSessionDetails,
        }),
      )
      return
    }

    await dispatch(
      saveTeacherTimetable({
        t,
        action: timetableSessionDropped({
          timetableSessionId,
          sessionDetails: { startTime, day },
        }),
      }),
    )
  }
}

export const removeTimetableSession = (
  payload: RemoveTimetableSessionPayload,
  t: TFunction<readonly ["timetables"]>,
): PageThunk<void> => {
  return async (dispatch, _getState) => {
    await dispatch(
      saveTeacherTimetable({ action: timetableSessionRemoved(payload), t }),
    )
  }
}

export const adjustTimetablePriority = (
  payload: AdjustTimetablePriorityPayload,
  t: TFunction<readonly ["timetables"]>,
): PageThunk<void> => {
  return async (dispatch) =>
    await dispatch(
      saveTeacherTimetable({ action: timetablePriorityAdjusted(payload), t }),
    )
}

export const updateTimetableTimezone = timetableTimezoneUpdated
